import { z } from 'zod';

/* ------------------------------------------------------------------ */
/*  Domain enums                                                       */
/* ------------------------------------------------------------------ */

export const DomainKeyEnum = z.enum([
  'userManagement',
  'vulnerabilityScanning',
  'mcpServerManagement',
  'agentServerManagement',
  'auditLog',
]);
export type DomainKey = z.infer<typeof DomainKeyEnum>;

export const LevelEnum = z.enum(['none', 'view', 'full']);
export type Level = z.infer<typeof LevelEnum>;

/* ------------------------------------------------------------------ */
/*  Core schemas                                                       */
/* ------------------------------------------------------------------ */

export const PermissionsSchema = z.record(DomainKeyEnum, LevelEnum);
export type Permissions = z.infer<typeof PermissionsSchema>;

export const RoleSchema = z.object({
  roleId: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: PermissionsSchema,
  memberCount: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Role = z.infer<typeof RoleSchema>;

export const RoleMemberSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string(),
});
export type RoleMember = z.infer<typeof RoleMemberSchema>;

export const RoleDetailSchema = RoleSchema.extend({
  members: z.array(RoleMemberSchema),
});
export type RoleDetail = z.infer<typeof RoleDetailSchema>;

/* ------------------------------------------------------------------ */
/*  Response wrappers                                                  */
/* ------------------------------------------------------------------ */

export const RoleListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(RoleSchema),
});

export const RoleDetailResponseSchema = z.object({
  success: z.boolean(),
  data: RoleDetailSchema,
});

export const RoleMutationResponseSchema = z.object({
  success: z.boolean(),
  data: RoleSchema,
});

export const RoleDeleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/* ------------------------------------------------------------------ */
/*  Mutation payloads (for type-safe call-sites)                       */
/* ------------------------------------------------------------------ */

export interface CreateRoleBody {
  name: string;
  description?: string;
  permissions: Record<DomainKey, Level>;
}

export interface UpdateRoleBody {
  name?: string;
  description?: string;
  permissions?: Record<DomainKey, Level>;
}
